import { FilterFn } from "@tanstack/react-table"
import { ColumnFilterValue, NumberFormatConfig, SEPARATOR_OPTIONS } from "@/types/editor"

export const customFilterFn: FilterFn<Record<string, unknown>> = (row, columnId, filterValue: unknown) => {
  const f = filterValue as ColumnFilterValue | undefined
  if (!f?.type) return true
  const raw = row.getValue(columnId)
  const cell = raw == null ? "" : String(raw)
  const v = (f.value ?? "").trim().toLowerCase()
  switch (f.type) {
    case "contains":
      return cell.toLowerCase().includes(v)
    case "doesNotContain":
      return !cell.toLowerCase().includes(v)
    case "is":
      return cell === (f.value ?? "")
    case "isNot":
      return cell !== (f.value ?? "")
    case "isEmpty":
      return raw == null || cell.trim() === ""
    case "isNotEmpty":
      return raw != null && cell.trim() !== ""
    default:
      return true
  }
}

export const customGlobalFilterFn: FilterFn<Record<string, unknown>> = (row, _columnId, filterValue) => {
  const s = typeof filterValue === "string" ? filterValue.trim().toLowerCase() : ""
  if (!s) return true
  return row.getVisibleCells().some((c) =>
    String(c.getValue() ?? "")
      .toLowerCase()
      .includes(s)
  )
}

export const formatNumberWithSeparators = (
  num: number,
  sep: (typeof SEPARATOR_OPTIONS)[number],
  decimalPlaces: number
): string => {
  const mult = 10 ** decimalPlaces
  const rounded = Math.round(num * mult) / mult
  const [intPart, decPart] = rounded.toFixed(decimalPlaces).split(".")
  const withThousands =
    intPart.replace(/\B(?=(\d{3})+(?!\d))/g, sep.thousands) +
    (decimalPlaces > 0 ? sep.decimal + (decPart || "0".repeat(decimalPlaces)) : "")
  return withThousands
}

export const formatNumber = (raw: unknown, config: Partial<NumberFormatConfig> | undefined): string | null => {
  if (config == null) return null
  const n =
    typeof raw === "number" && !Number.isNaN(raw)
      ? raw
      : typeof raw === "string" && raw !== "" && !Number.isNaN(Number(raw))
        ? Number(raw)
        : null
  if (n == null) return null

  const style = config.style ?? "normal"
  const separatorStyle = config.separatorStyle ?? "100,000.00"
  const decimalPlaces = config.decimalPlaces ?? 2
  const multiplyBy = config.multiplyBy ?? 1
  const prefix = config.prefix ?? ""
  const suffix = config.suffix ?? ""

  let val = n * multiplyBy
  if (style === "percent") val = val * 100

  const sep = SEPARATOR_OPTIONS.find((s) => s.id === separatorStyle) ?? SEPARATOR_OPTIONS[0]
  let s: string
  if (style === "scientific") {
    s = val.toExponential(decimalPlaces)
  } else {
    s = formatNumberWithSeparators(val, sep, decimalPlaces)
  }
  const suffixOut = style === "percent" && (config.suffix ?? "") === "" ? " %" : suffix
  return prefix + s + suffixOut
}
